import type { Video } from 'expo-av';
import type { RefObject } from 'react';
import {
  useSharedValue,
  withTiming,
  SharedValue,
} from 'react-native-reanimated';

import { IS_WEB } from '@utils';

const TIMING_DURATION = 500;
const FIVE_SECONDS = 5000;

export function useVideoPlayerRewind(videoRef: RefObject<Video>) {
  const leftRewindArrowOpacity = useSharedValue(0);
  const rightRewindArrowOpacity = useSharedValue(0);

  // Return status only when video is loaded
  async function getStatusAsync() {
    const status = await videoRef.current?.getStatusAsync();

    if (status?.isLoaded) {
      // On web isLoaded always will be true even if a video is not loaded
      // so to define if the video is loaded we check durationMillis
      // if it is NaN it means that the video is not loaded
      if (IS_WEB && !Number.isNaN(status.durationMillis)) {
        return status;
      } else if (!IS_WEB) {
        return status;
      }
    }

    return;
  }

  function animateArrow(arrowOpacity: SharedValue<number>) {
    arrowOpacity.value = withTiming(
      1,
      {
        duration: TIMING_DURATION,
      },
      (finished) => {
        if (finished) {
          arrowOpacity.value = withTiming(0, {
            duration: TIMING_DURATION,
          });
        }
      },
    );
  }

  async function rewindBackward() {
    const status = await getStatusAsync();

    if (status) {
      videoRef.current?.setPositionAsync(
        Math.max(status.positionMillis - FIVE_SECONDS, 0),
      );

      animateArrow(leftRewindArrowOpacity);
    }
  }

  async function rewindForward() {
    const status = await getStatusAsync();

    if (status) {
      const maxDurationMills = status.durationMillis;

      const preferableNewPosition =
        status.positionMillis + FIVE_SECONDS;
      const newPosition =
        typeof maxDurationMills === 'number' &&
        !Number.isNaN(maxDurationMills)
          ? Math.min(preferableNewPosition, maxDurationMills)
          : preferableNewPosition;

      videoRef.current?.setPositionAsync(newPosition);

      animateArrow(rightRewindArrowOpacity);
    }
  }

  return {
    rewindBackward,
    rewindForward,
    leftArrowOpacity: leftRewindArrowOpacity,
    rightArrowOpacity: rightRewindArrowOpacity,
  };
}
